import { createMediaSyncDiagnosticRecorder } from './mediaDiagnosticRuntime';
import { sanitizeMediaRef } from './MediaDiagnosticHistory';

type SendContext = {
  companyId: string; userId: string; checklistId: string; operationId: string;
  transport: 'legacy_rest' | 'rpc'; values: readonly Record<string, any>[];
  pendings: readonly Record<string, any>[];
};

// A correlação usa o caminho inteiro; o corte de180 caracteres serve apenas à exportação.
const fullMediaPath = (value: unknown): string | undefined => typeof value === 'string'
  ? value.split(/[?#]/)[0].match(/(?:^|\/)(checklists?-(?:photos|signatures|marcacoes)\/[A-Za-z0-9._/-]+)$/)?.[1]
  : undefined;

/** Observa o request já previsto, sem consultas extras nem interpretação do ACK de negócio. */
export async function observeChecklistMediaReferenceSend<T extends { data?: unknown; error?: unknown }>(
  input: SendContext, send: () => PromiseLike<T>,
): Promise<T> {
  const records: ReturnType<typeof createMediaSyncDiagnosticRecorder>[] = [];
  try {
    for (const value of input.values) {
      const mediaRef = sanitizeMediaRef(value.valor);
      if (!mediaRef) continue;
      const fullPath = fullMediaPath(value.valor);
      const candidates = input.pendings.filter(pending => pending.item_id === input.checklistId &&
        (!pending.empresa_id || pending.empresa_id === input.companyId) &&
        typeof pending.campo_id === 'string' && Boolean(pending.campo_id) &&
        (pending.campo_id === value.campo_id || pending.campo_id === value.snapshot_field_id) &&
        (pending.prepared_public_url === value.valor || (fullPath !== undefined && fullMediaPath(pending.path) === fullPath)));
      // Ambiguidade permanece explícita pela ausência de attemptId; não inventar autoria/correlação.
      const pending = candidates.length === 1 ? candidates[0] : undefined;
      records.push(createMediaSyncDiagnosticRecorder({
        companyId: input.companyId, userId: input.userId, checklistId: input.checklistId,
        operationId: input.operationId, transport: input.transport,
        fieldId: pending?.campo_id ?? value.snapshot_field_id ?? value.campo_id,
        attemptId: pending?.id, valueId: value.id, mediaRef,
      }));
    }
  } catch { /* Metadado de diagnóstico não impede o request original. */ }
  const record = (phase: Parameters<(typeof records)[number]>[0], reason?: Parameters<(typeof records)[number]>[1], error?: unknown) => {
    for (const write of records) { try { write(phase, reason, error); } catch { /* Fail-open inclusive no emissor. */ } }
  };
  record('reference_send_started');
  try {
    const result = await send();
    record(result.error ? 'reference_send_failed' : 'reference_send_returned', result.error ? 'request_failed' : 'response_received', result.error);
    return result;
  } catch (error) {
    record('reference_send_failed', 'request_failed', error);
    throw error;
  }
}
